import { useEffect, useState } from "react";
import { ExternalLink, Award } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * FacultyHighlights — 3-4 star professors + research strands per uni
 * for the Teaching tab. Reads
 * `/api/universities/{id}/faculty-highlights`. Sourced from a curated
 * seed.
 *
 * Each row: name (link to uni profile) + department + one-line
 * research summary. Monogram avatar so the layout stays uniform
 * without portrait rights headaches.
 */
export default function FacultyHighlights({ uniId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/faculty-highlights`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  const rows = data?.faculty || [];
  if (rows.length === 0) return null;

  return (
    <section className="mb-8" data-testid="faculty-highlights">
      <div>
        <TabKicker>03 · Faculty Highlights</TabKicker>
        <TabSectionTitle>Who you'd learn from</TabSectionTitle>
      </div>

      <div className="border border-[#002147]/10 bg-white/40 divide-y divide-[#002147]/10">
        {rows.map((f) => (
          <a
            key={f.name}
            href={f.profile_url || "#"}
            target="_blank" rel="noopener noreferrer"
            className="p-4 flex gap-3 items-start hover:bg-[#F3F1EC]/50 transition-colors group"
            data-testid={`faculty-${f.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <div className="shrink-0 w-11 h-11 bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center">
              <Award className="h-4 w-4 text-[#7C3AED]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-serif text-base text-[#002147] leading-tight group-hover:text-[#7C3AED] transition-colors">
                  {f.name}
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50 shrink-0">
                  {f.department}
                </span>
              </div>
              <div className="mt-1 font-sans text-sm text-[#002147]/75 leading-snug">
                {f.research}
              </div>
            </div>
            {f.profile_url && (
              <ExternalLink className="h-3 w-3 shrink-0 mt-1 text-[#002147]/30 group-hover:text-[#7C3AED] transition-colors" />
            )}
          </a>
        ))}
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
        Editorial selection · profile links to each professor's uni page
      </p>
    </section>
  );
}
